import React from 'react';
import { Info, BarChart2, Wallet, PiggyBank, Target, AlertTriangle, TrendingUp, Users } from 'lucide-react';

/**
 * Компонент с информационным содержимым о семейном бюджете
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о семейном бюджете: основы, методы, советы
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-500" />
          Что такое семейный бюджет?
        </h3>
        <p>
          <strong>Семейный бюджет</strong> — это финансовый план, который помогает отслеживать и контролировать доходы и расходы семьи в течение определённого периода времени (обычно месяц). Он позволяет эффективно распределять средства, избегать лишних трат, создавать накопления и достигать финансовых целей.
        </p>
        <p>
          Семейный бюджет не только организует ваши финансы, но и помогает всем членам семьи лучше понимать финансовое положение и совместно принимать обоснованные решения о расходах, сбережениях и инвестициях. Это особенно важно в России, где экономическая ситуация может быть нестабильной, а финансовое планирование становится необходимым инструментом для обеспечения благополучия семьи.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-emerald-500" />
          Популярные методы ведения бюджета
        </h3>
        <div className="mb-6">
          <h4>1. Метод 50/30/20</h4>
          <p>
            Один из самых популярных методов распределения бюджета во всем мире, который также хорошо адаптируется к российским реалиям. Согласно этому методу, ваш доход делится на три категории:
          </p>
          <ul>
            <li><strong>50% — на необходимые расходы:</strong> жилье, питание, транспорт, коммунальные услуги, связь, обязательные платежи по кредитам.</li>
            <li><strong>30% — на желания и развлечения:</strong> рестораны, кино, покупка одежды, гаджеты, хобби, подарки и другие необязательные расходы.</li>
            <li><strong>20% — на финансовые цели:</strong> создание чрезвычайного фонда, погашение долгов, инвестиции и накопления.</li>
          </ul>
          <p>
            Этот метод прост в использовании и даёт достаточную гибкость, но для многих российских семей может потребоваться корректировка пропорций из-за высокой доли расходов на жильё в крупных городах.
          </p>
        </div>

        <div className="mb-6">
          <h4>2. Метод конвертов</h4>
          <p>
            Традиционный метод, который был популярен ещё в советское время и остаётся актуальным для тех, кто предпочитает наличные расчёты или склонен к импульсивным тратам:
          </p>
          <ul>
            <li>В начале месяца распределите наличные деньги по конвертам, каждый из которых соответствует определенной категории расходов (продукты, транспорт, развлечения и т.д.).</li>
            <li>Когда деньги в конверте заканчиваются, расходы по этой категории прекращаются до следующего месяца.</li>
            <li>В современной версии вместо физических конвертов используются отдельные счета, карты или мобильные приложения.</li>
          </ul>
          <p>
            Этот метод особенно эффективен для тех, кто только начинает вести бюджет и хочет выработать финансовую дисциплину.
          </p>
        </div>

        <div className="mb-6">
          <h4>3. Метод "сначала заплати себе"</h4>
          <p>
            Этот подход особенно полезен для формирования привычки к сбережениям:
          </p>
          <ul>
            <li>Как только вы получаете доход, немедленно откладывайте определенный процент (обычно 10-20%) на сбережения или инвестиции.</li>
            <li>Оставшуюся сумму используйте для текущих расходов.</li>
            <li>Этот метод обеспечивает приоритет сбережений перед расходами.</li>
          </ul>
          <p>
            В России этот метод может быть особенно полезен, так как помогает создать финансовую "подушку безопасности", что критически важно в условиях экономической нестабильности.
          </p>
        </div>

        <div className="mb-6">
          <h4>4. Метод "нулевого бюджета"</h4>
          <p>
            По этой методике каждый рубль должен быть распределен так, чтобы доходы минус расходы равнялись нулю:
          </p>
          <ul>
            <li>Распределите весь доход по категориям расходов, сбережений и инвестиций.</li>
            <li>В конце планирования не должно остаться нераспределенных средств.</li>
            <li>Каждый рубль имеет свое предназначение до того, как он получен.</li>
          </ul>
          <p>
            Этот метод особенно эффективен для семей с нестабильным доходом или для тех, кто хочет максимально оптимизировать свои расходы.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-emerald-500" />
          Особенности финансового планирования в России
        </h3>
        <p>
          Планирование семейного бюджета в России имеет свои особенности, которые важно учитывать:
        </p>
        <ul>
          <li><strong>Высокая инфляция:</strong> В России уровень инфляции часто выше, чем в развитых странах. Это означает, что деньги, хранящиеся в рублях, быстрее теряют покупательную способность. Поэтому важно учитывать инфляцию при планировании сбережений и инвестиций.</li>
          <li><strong>Сезонные расходы:</strong> В России есть ряд сезонных расходов, которые нужно учитывать в годовом планировании — подготовка к отопительному сезону, сезонная одежда из-за резких климатических различий, дачный сезон, подготовка к учебному году.</li>
          <li><strong>Высокая доля расходов на жилье:</strong> В крупных городах России (особенно в Москве и Санкт-Петербурге) стоимость жилья и аренды может составлять значительную часть бюджета, превышая рекомендуемые 30%.</li>
          <li><strong>Система налоговых вычетов:</strong> Россия предлагает различные налоговые вычеты (имущественный, социальный, инвестиционный), которые могут существенно снизить налоговую нагрузку. Их использование должно быть частью финансового планирования.</li>
          <li><strong>Значение чрезвычайного фонда:</strong> В условиях экономической нестабильности особенно важно иметь чрезвычайный фонд, покрывающий 3-6 месячные расходы семьи.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные финансовые инструменты для российских семей
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 mt-2 space-y-1">
            <li><strong>Индивидуальный инвестиционный счет (ИИС):</strong> позволяет получать налоговый вычет при инвестировании.</li>
            <li><strong>Накопительные счета и вклады в банках:</strong> как инструмент защиты от инфляции с минимальным риском.</li>
            <li><strong>Программы накопительного страхования жизни:</strong> сочетают защиту и накопления.</li>
            <li><strong>Государственные программы поддержки:</strong> материнский капитал, социальная ипотека, субсидии для молодых семей.</li>
            <li><strong>Мультивалютная корзина:</strong> распределение сбережений между разными валютами для снижения валютных рисков.</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-500" />
          Финансовые цели и их планирование
        </h3>
        <p>
          Установка финансовых целей — важнейшая часть семейного бюджетирования. Цели должны быть конкретными, измеримыми, достижимыми, актуальными и ограниченными по времени (SMART).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h4>Краткосрочные цели (до 1 года)</h4>
            <ul>
              <li>Создание чрезвычайного фонда</li>
              <li>Погашение дорогих кредитов</li>
              <li>Покупка бытовой техники</li>
              <li>Отпуск</li>
              <li>Ремонт квартиры</li>
            </ul>
          </div>
          <div>
            <h4>Долгосрочные цели (более 1 года)</h4>
            <ul>
              <li>Накопление на первоначальный взнос по ипотеке</li>
              <li>Образование детей</li>
              <li>Покупка автомобиля</li>
              <li>Пенсионные накопления</li>
              <li>Создание пассивного дохода</li>
            </ul>
          </div>
        </div>
        <p>
          Для каждой цели важно определить:
        </p>
        <ol>
          <li>Конкретную сумму, которую нужно накопить</li>
          <li>Срок, к которому нужно накопить эту сумму</li>
          <li>Ежемесячный взнос, необходимый для достижения цели</li>
          <li>Финансовый инструмент для накопления, соответствующий сроку и уровню риска</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          Инвестиции в семейном бюджете
        </h3>
        <p>
          Инвестирование — важный компонент долгосрочного финансового планирования. Для российских семей доступны различные инвестиционные инструменты с разным уровнем риска:
        </p>
        <ul>
          <li><strong>Вклады и накопительные счета в банках:</strong> низкий риск, защита государственной системой страхования вкладов до 1,4 млн рублей.</li>
          <li><strong>Облигации федерального займа (ОФЗ):</strong> государственные ценные бумаги с низким риском.</li>
          <li><strong>Корпоративные облигации:</strong> более высокая доходность и умеренный риск.</li>
          <li><strong>Акции российских и зарубежных компаний:</strong> потенциально высокая доходность при высоком риске.</li>
          <li><strong>Паевые инвестиционные фонды (ПИФы) и ETF:</strong> позволяют инвестировать в диверсифицированный портфель ценных бумаг.</li>
          <li><strong>Недвижимость:</strong> как инвестиция с потенциалом роста стоимости и возможностью получения арендного дохода.</li>
        </ul>
        <p>
          Важно помнить, что инвестиции следует рассматривать только после формирования чрезвычайного фонда и погашения дорогих кредитов. Инвестиционная стратегия должна соответствовать финансовым целям, временному горизонту и толерантности к риску.
        </p>

        <h3 className="flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-500" />
          Вовлечение всей семьи в планирование бюджета
        </h3>
        <p>
          Для успешного ведения семейного бюджета важно вовлечь всех взрослых членов семьи, а также постепенно обучать детей финансовой грамотности:
        </p>
        <ol>
          <li><strong>Регулярно проводите семейные финансовые советы</strong> для обсуждения доходов, расходов, целей и корректировки бюджета.</li>
          <li><strong>Распределите финансовые роли в семье</strong> с учетом навыков и интересов каждого члена (один ведет учет, другой планирует инвестиции и т.д.).</li>
          <li><strong>Обучайте детей основам финансовой грамотности</strong> через карманные деньги, совместные покупки и объяснение финансовых решений.</li>
          <li><strong>Создайте общий доступ к информации о бюджете</strong> — используйте совместные таблицы, приложения или семейный финансовый календарь.</li>
          <li><strong>Отмечайте финансовые успехи всей семьей</strong> — достижение целей, увеличение сбережений, погашение кредитов.</li>
        </ol>
        <p>
          Помните, что ведение семейного бюджета — это не цель, а инструмент для достижения финансового благополучия и реализации семейных целей. Правильное финансовое планирование снижает стресс, связанный с деньгами, и помогает построить более стабильное и обеспеченное будущее.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор семейного бюджета поможет вам систематизировать финансы, определить оптимальное распределение средств и спланировать достижение важных целей!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;